<template>
  <div class="dashboard">
    <div v-if="!isFirstBroadcast && !loadingOffer && !offerExpired && offerPlan" class="dashboard-banner">
      <PlanOfferBanner
        :offer="offer"
        :plan="offerPlan"
        :user-name="user.name"
        :on-click="toggleDialog"
      />
    </div>

    <div class="dashboard-content">
      <h4 class="dashboard-title">
        Dashboard
      </h4>

      <section id="status">
        <BannerStatusList />
      </section>
      <section v-if="zap.id" id="broadcast">
        <BroadcastBanner
          v-if="isFirstBroadcast && loggedInUser.is_free"
          :plan="offerPlan"
          :plans="plans"
          :on-click="toggleDialog"
        />
        <div v-else class="broadcast-section">
          <BroadcastHistory />
          <BroadcastReport />
        </div>
      </section>
    </div>

    <PlanDialog
      v-if="!loadingOffer && offerPlan"
      :loading="loading"
      :offer="offer"
      :plans="plans"
      :open="dialog"
      :type="dialogType"
      @onSelectPlan="setPlanSelected"
      @togglePlan="dialogType = PlanDialogType.ALL_PLANS"
      @close="toggleDialog"
    />

    <!-- <DialogTour v-if="!applicationTourCompleted" @click="$tours['dashboard-tour'].start()" /> -->

    <BannerPopup
      v-if="!loggedInUser.is_free"
      :dialog="showBannerPopup"
      :is-free="false"
      @close="closeBannerPopup"
    />

    <Tour
      name="dashboard-tour"
      :steps="tour.steps"
      :options="tour.options"
      :callbacks="tour.callbacks"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import Tour from '@/components/base/tour'

import BannerStatusList from '@/components/pages/dashboard/banner-status-list'
import BroadcastBanner from '@/components/pages/dashboard/broadcast-banner'
import BroadcastHistory from '@/components/pages/dashboard/broadcast-history'
import BroadcastReport from '@/components/pages/dashboard/broadcast-report'
import PlanDialog from '@/components/pages/dashboard/plan-dialog'
import PlanOfferBanner from '@/components/pages/dashboard/plan-offer-banner'
import { PlanDialogType } from '@/components/pages/dashboard/plan-dialog'
import BannerPopup from '@/components/modal/banner-popup'

const steps = [
  {
    target: '.banner-status-list .root:nth-child(1) .banner',
    content: `Número de WhatsApp que está conectado à Clickzap.`,
    params: {
      placement: 'top'
    }
  },
  {
    target: '.banner-status-list .root:nth-child(2) .banner',
    content: `Número de contatos que você possui e limite do seu plano. Nós informaremos caso você alcance esse limite.`,
    params: {
      placement: 'top'
    }
  },
  {
    target: '.banner-status-list .root:nth-child(3) .banner',
    content: `Total de transmissões que você enviou desde o início.`,
    params: {
      placement: 'top'
    }
  },
  {
    target: '.broadcast-history',
    content: `As três últimas transmissões que você enviou aparecem aqui. Para ver mais, clique no botão "Ver tudo" à direita.`,
    params: {
      placement: 'top'
    }
  },
  {
    target: '.broadcast-report',
    content: `Gráfico do relatório detalhado de envios de transmissões dos últimos 7 dias.`,
    params: {
      placement: 'top'
    }
  },
  {
    target: '.navbar-logo-wrapper svg',
    content: `Este é o novo menu. Clique no ícone de "menu" para ver todas as opções, ou passe o mouse pelos ícones abaixo.`
  }
]

export default {
  layout: 'dashboard',
  components: {
    BannerStatusList,
    BroadcastBanner,
    BroadcastHistory,
    BroadcastReport,
    PlanDialog,
    PlanOfferBanner,
    Tour,
    BannerPopup
  },
  data: () => ({
    applicationTourCompleted: true,
    dialog: false,
    dialogType: PlanDialogType.TRIAL_OFFER,
    PlanDialogType: PlanDialogType,
    loading: false,
    tour: { callbacks: {}, steps },
    showBannerPopup: false
  }),
  computed: {
    ...mapState({
      user: state => state.auth?.user,
      offer: state => state.plans.offer?.data || {},
      plans: state => state.plans.plans?.data || [],
      zap: state => state.zaps.zap || {}
    }),
    ...mapGetters({
      loadingOffer: 'plans/loadingOffer',
      loadingPlans: 'plans/loadingPlans',
      loggedInUser: 'loggedInUser'
    }),
    isFirstBroadcast() {
      return (
        this.zap?.total_broadcasts_sended === 0 &&
        this.zap?.total_broadcasts_not_finished == 0
      )
    },
    offerPlan() {
      if (!this.plans?.length) return undefined

      return this.plans.find(plan => plan.id === this.offer.preferred_plan_id)
    },
    offerExpired() {
      const now = new Date().getTime()
      const expiration = new Date(this.offer.expires_at).getTime()
      return expiration <= now
    }
  },
  async created() {
    const { main_offer_id: mainOffer } = this.user

    const shouldLoadOffer = Boolean(mainOffer)
    const shouldLoadPlans = Boolean(mainOffer)

    if (shouldLoadOffer && this.loadingOffer) {
      await this.getOffer({ mainOffer })
    }

    if (shouldLoadPlans && this.loadingPlans) {
      console.log(this.offer)
      this.getPlans(this.offer?.offer_id)
    }
  },
  async mounted() {
    this.$mixpanel.track('[dashboard] Open Dashboard Page')
    this.applicationTourCompleted = await this.$localForage.applicationTour.getItem(
      'completed'
    )

    // Verificar quantas vezes o banner já foi exibido e se o usuário NÃO é free
    const bannerShowCount = parseInt(localStorage.getItem('bannerPopupShowCount') || '0')
    if (bannerShowCount < 5 && !this.loggedInUser.is_free) {
      this.showBannerPopup = true
    }

    if (this.user.is_free) {
      this.tour.steps = this.tour.steps.map(step => {
        if (step.target === '.banner-status-list .root:nth-child(3) .banner') {
          return {
            ...step,
            content:
              'Total de transmissões enviadas e disponíveis para enviar durante o teste grátis.'
          }
        }

        if (step.target === '.broadcast-history') {
          return {
            ...step,
            content:
              'As três últimas transmissões que você enviou aparecem aqui.'
          }
        }

        return { ...step }
      })
    }

    this.tour.steps = this.tour.steps.filter(step =>
      Boolean(document.querySelector(step.target))
    )
  },
  methods: {
    ...mapActions({
      getOffer: 'plans/getOffer',
      getPlans: 'plans/getPlans',
      updateSubscriptionPlan: 'subscription/updateSubscriptionPlan'
    }),
    toggleDialog() {
      this.dialogType = PlanDialogType.TRIAL_OFFER
      this.dialog = !this.dialog
      if (this.dialog) {
        this.$mixpanel.track('[payment] Offer Banner Button Clicked', {
          offer: this.offer
        })
      }
    },
    async setPlanSelected(identifier) {
      this.loading = true
      this.$mixpanel.track('[payment] Dialog Plan Selected', {
        plan: identifier
      })
      this.$router.push(`/payment?plan=${identifier}`)

      setTimeout(() => {
        this.loading = false
        this.toggleDialog()
      }, 2000)
    },
    closeBannerPopup() {
      this.showBannerPopup = false
      // Incrementar o contador de exibições do banner
      const currentCount = parseInt(localStorage.getItem('bannerPopupShowCount') || '0')
      localStorage.setItem('bannerPopupShowCount', String(currentCount + 1))
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'assets/scss/variables';

.dashboard {
  background-color: $neutral100;

  &-title {
    @include HeadingXSmall;
    font-weight: 600;
    font-family: $monserratFontFamily;
    color: $neutral500;

    margin-bottom: $scale900;
  }

  &-content {
    margin-top: 8px;

    .broadcast-section {
      display: grid;
      grid-template-columns: 1fr;
      margin-top: 70px;
      row-gap: 40px;

      @include media-query($medium-breakpoint) {
        row-gap: 80px;
      }

      @include media-query($large-breakpoint) {
        grid-template-columns: 5fr 7fr;
        column-gap: 40px;
      }
    }
  }
}

.dashboard-banner {
  margin-top: 24px;
  margin-bottom: 32px;
}

::v-deep .card-template {
  height: 90%;
}
</style>
